import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuickSearch } from '../../hooks/useSearch';
import type { QuickSearchResult } from '../../services/api/search';
import { Button } from '../ui';

// Pillar labels for display
const PILLAR_LABELS: Record<string, string> = {
  command: 'Command',
  chronicle: 'Chronicle',
  connect: 'Connect',
};

// Type labels for display
const TYPE_LABELS: Record<string, string> = {
  task: 'Task',
  protocol: 'Protocol',
  channel_post: 'Post',
  journal: 'Journal',
};

// Icons per type (using simple text icons)
const TYPE_ICONS: Record<string, string> = {
  task: 'T',
  protocol: 'P',
  channel_post: 'C',
  journal: 'J',
};

const PILLAR_COLORS: Record<string, string> = {
  command: 'bg-[var(--theme-pillar-command)]',
  chronicle: 'bg-[var(--theme-pillar-chronicle)]',
  connect: 'bg-[var(--theme-pillar-connect)]',
};

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data, isLoading } = useQuickSearch(debouncedQuery, 8);
  const results = useMemo(() => data?.results || [], [data?.results]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setSelectedIndex(0);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          onClose();
        }
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const navigateToResult = useCallback((result: QuickSearchResult) => {
    onClose();
    switch (result.type) {
      case 'task':
        navigate('/tasks');
        break;
      case 'protocol':
        navigate('/command/protocols');
        break;
      case 'channel_post':
        if (result.id) {
          navigate('/connect');
        }
        break;
      case 'journal':
        navigate(`/journal/${result.id}`);
        break;
    }
  }, [navigate, onClose]);

  const navigateToFullSearch = useCallback(() => {
    if (query.trim().length >= 2) {
      onClose();
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  }, [query, navigate, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex === results.length) {
        navigateToFullSearch();
      } else if (results[selectedIndex]) {
        navigateToResult(results[selectedIndex]);
      } else {
        navigateToFullSearch();
      }
    }
  }, [results, selectedIndex, navigateToResult, navigateToFullSearch]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex justify-center bg-black/50 dark:bg-black/70 backdrop-blur-sm animate-fade-in pt-0 md:pt-[15vh] items-stretch md:items-start"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Search"
    >
      <div
        className="w-full bg-bg-primary border border-border-light shadow-[0_16px_48px_rgba(0,0,0,0.2)] dark:shadow-[0_16px_48px_rgba(0,0,0,0.5)] overflow-hidden animate-slide-up max-w-full md:max-w-[560px] mx-0 md:mx-4 rounded-none md:rounded-xl h-full md:h-auto flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 px-4 py-3.5 border-b border-border-light">
          <span className="shrink-0 text-text-secondary flex items-center" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
          </span>
          <input
            ref={inputRef}
            type="text"
            className="flex-1 border-none outline-none bg-transparent text-base text-text-primary font-[inherit] placeholder:text-text-tertiary"
            placeholder="Search tasks, protocols, posts, journal..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="shrink-0 px-1.5 py-0.5 text-[11px] font-[inherit] text-text-tertiary bg-bg-secondary border border-border-light rounded hidden md:block">ESC</kbd>
        </div>

        {debouncedQuery.length >= 2 && (
          <div className="overflow-y-auto flex-1 max-h-none md:max-h-[360px]">
            {isLoading && (
              <div className="py-6 px-4 text-center text-text-secondary text-sm">Searching...</div>
            )}

            {!isLoading && results.length === 0 && (
              <div className="py-6 px-4 text-center text-text-secondary text-sm">
                No results for &ldquo;{debouncedQuery}&rdquo;
              </div>
            )}

            {!isLoading && results.length > 0 && (
              <>
                <ul className="list-none m-0 py-1" role="listbox">
                  {results.map((result: QuickSearchResult, index: number) => (
                    <li
                      key={`${result.type}-${result.id}`}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${index === selectedIndex ? 'bg-[var(--theme-bg-hover)] dark:bg-[var(--theme-bg-hover)]' : ''}`}
                      role="option"
                      aria-selected={index === selectedIndex}
                      onClick={() => navigateToResult(result)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <span className={`shrink-0 flex items-center justify-center w-7 h-7 rounded-md text-xs font-semibold text-text-on-accent ${PILLAR_COLORS[result.pillar] || 'bg-accent'}`}>
                        {TYPE_ICONS[result.type] || '?'}
                      </span>
                      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                        <span className="text-sm font-medium text-text-primary whitespace-nowrap overflow-hidden text-ellipsis">{result.title}</span>
                        <span className="text-xs text-text-secondary">
                          {TYPE_LABELS[result.type] || result.type}
                          {' in '}
                          {PILLAR_LABELS[result.pillar] || result.pillar}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={navigateToFullSearch}
                  onMouseEnter={() => setSelectedIndex(results.length)}
                  className={`w-full py-3 px-4 border-t border-border-light text-[var(--theme-link)] text-[13px] font-medium rounded-none${selectedIndex === results.length ? ' bg-[var(--theme-bg-hover)] dark:bg-[var(--theme-bg-hover)]' : ''}`}
                >
                  View all results for &ldquo;{debouncedQuery}&rdquo;
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
